import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  ProgramInfo,
  SessionInfo,
  AttendeeInfo,
  AttendeeProgramInfo,
  AttendeeSessionInfo,
} from '@/types/attendance'
import type { MealPeriodInfo } from '@/types/capacity'

/**
 * sessionStore.ts
 * TypeScript port of Helper/SessionName.cs (Blazored.SessionStorage replacement).
 * All 20 session keys are typed; state persists to sessionStorage via Zustand persist.
 *
 * Source: src/Presentation/Deloitte.DU.DigitalBadging.Web/Helper/SessionName.cs
 */

// ---------------------------------------------------------------------------
// Session state shape — mirrors SessionName.cs constants
// ---------------------------------------------------------------------------
export interface SessionState {
  /** SessionName.EventDetails — selected event details */
  eventDetails: Record<string, unknown> | null
  /** SessionName.SelectedDates — dashboard filter date range */
  selectedDates: { start: string; end: string } | null
  /** SessionName.UserRoleID */
  userRoleId: string
  /** SessionName.RequestedURL — redirect target after login */
  requestedUrl: string
  /** SessionName.Alias */
  alias: string
  /** SessionName.LoggedInUserEmail */
  loggedInUserEmail: string
  /** SessionName.LoggedInUserName */
  loggedInUserName: string
  /** SessionName.ProgramDetails — selected program object */
  programDetails: ProgramInfo | null
  /** SessionName.SessionDetails — selected session object */
  sessionDetails: SessionInfo | null
  /** SessionName.AttendeeDetails — selected attendee object */
  attendeeDetails: AttendeeInfo | null
  /** SessionName.AttendeeProgramInfo */
  attendeeProgramInfo: AttendeeProgramInfo | null
  /** SessionName.AttendeeSessionInfo */
  attendeeSessionInfo: AttendeeSessionInfo | null
  /** SessionName.ActiveTabIndex — active tab in TabStrip components */
  activeTabIndex: number
  /** SessionName.MealPeriodInfo */
  mealPeriodInfo: MealPeriodInfo | null
  /** SessionName.IsSessionLogTab — dashboard view toggle */
  isSessionLogTab: boolean
  /** SessionName.IsReloadGraph */
  isReloadGraph: boolean
  /** SessionName.ShowOnlySessionWithScan */
  showOnlySessionWithScan: boolean
  /** SessionName.ShowOnlyRegisteredAttendees */
  showOnlyRegisteredAttendees: boolean
  /** SessionName.ProgramID */
  programId: string
  /** SessionName.External */
  external: string
  /** SessionName.Tenant */
  tenant: string
}

interface SessionActions {
  set: <K extends keyof SessionState>(key: K, value: SessionState[K]) => void
  reset: () => void
}

const initialState: SessionState = {
  eventDetails: null,
  selectedDates: null,
  userRoleId: '',
  requestedUrl: '',
  alias: '',
  loggedInUserEmail: '',
  loggedInUserName: '',
  programDetails: null,
  sessionDetails: null,
  attendeeDetails: null,
  attendeeProgramInfo: null,
  attendeeSessionInfo: null,
  activeTabIndex: 0,
  mealPeriodInfo: null,
  isSessionLogTab: false,
  isReloadGraph: false,
  showOnlySessionWithScan: false,
  showOnlyRegisteredAttendees: false,
  programId: '',
  external: '',
  tenant: '',
}

export const useSessionStore = create<SessionState & SessionActions>()(
  persist(
    (set) => ({
      ...initialState,
      set: (key, value) => set((state) => ({ ...state, [key]: value })),
      reset: () => set(initialState),
    }),
    {
      name: 'du-session',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
